import {useEffect, useState} from "react";
import {Alert, Box, CircularProgress, Paper, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import TabOverview from "./distributor-tabs/tab-overview";
import TabAnalytics from "./distributor-tabs/tab-analytics";
import TabOurProducts from "./distributor-tabs/tab-our-products";
import TabProfileSettings from "./distributor-tabs/tab-profile-settings";
import TabMyCoupons from "./distributor-tabs/tab-my-coupons";
import axiosInstance from "../config/axios-config";
import {GetLogo} from "../profile/main/api/photo-data-handler";
import {PageContainer} from "../components/common/page-container";
import useDistributorProfile from "./use-distributor-profile";
import ProfileNavigationGuard from "./profile-navigation-guard";
import DistributorWorkspaceHeader from "./distributor-workspace-header";
import useDistributorNavigation from "./distributor-navigation";
import DistributorWorkspaceTabs from "./distributor-workspace-tabs";

export default function DistributorManagement({navOpen, tenantId, tenantType, userIdentity}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [distributor, setDistributor] = useState(null);
    const [logoUrl, setLogoUrl] = useState("");
    const [productWork, setProductWork] = useState(null);
    const profile = useDistributorProfile(tenantId, distributor, setDistributor);
    const {selected, selectTab, workflowRequest, workflowOpened} = useDistributorNavigation(tenantId);
    const tabValue = selected.panel;
    const saving = profile.saving || !!productWork?.saving;
    const select = (name, action) => { if (!saving) selectTab(name, action); };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setDistributor(null);
        axiosInstance.get(`/api/v1/tenants/${tenantId}`)
            .then(({data}) => { if (active) setDistributor(data); })
            .catch(() => { if (active) setDistributor(null); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [tenantId]);

    useEffect(() => {
        let active = true;
        setLogoUrl("");
        if (distributor?.imageUrl) GetLogo({filename: distributor.imageUrl})
            .then(url => { if (active) setLogoUrl(url || ""); })
            .catch(() => { if (active) setLogoUrl(""); });
        return () => { active = false; };
    }, [tenantId, distributor?.imageUrl]);

    const handleLogoUpdate = (updatedTenant, newLogoUrl) => {setDistributor(updatedTenant); setLogoUrl(newLogoUrl);};
    const onInputChange = (field, value) => profile.setDraft(previous => ({...previous, [field]: value}));
    const onAddressChange = (field, value) => profile.setDraft(previous => ({...previous,
        address: {...previous.address, [field]: value}}));

    return <PageContainer component="main" navOpen={navOpen}>
        <ProfileNavigationGuard dirty={profile.dirty || !!productWork?.dirty} saving={saving}
            titleKey={productWork?.dirty || productWork?.saving ? "ProductsLeaveTitle" : undefined}
            messageKey={productWork?.dirty ? "ProductsLeaveMessage" : undefined}
            waitKey={productWork?.saving ? "ProductsWaitMessage" : undefined}
            onDiscardNavigation={() => {
                if (profile.dirty) profile.toggleEdit();
                if (productWork?.dirty) productWork.discard();
            }}/>
        {profile.notice && <Alert severity={profile.notice.severity}
            role={profile.notice.severity === "success" ? "status" : "alert"} sx={{mb: 2}}>{profile.notice.message}</Alert>}
        {loading ? <Box py={6} display="flex" justifyContent="center"><CircularProgress aria-label={t("Loading")}/></Box>
            : distributor ? <>
                <DistributorWorkspaceHeader distributor={distributor} tenantType={tenantType} logoUrl={logoUrl}
                    disabled={saving} onOrders={() => navigate("/distributor/my-orders")}
                    onUpload={() => select("products", "upload")} onCoupon={() => select("coupons", "coupon")}/>
                <Paper variant="outlined" sx={{borderRadius: 2, mb: 2.5, minWidth: 0}}>
                    <DistributorWorkspaceTabs selected={selected.name} onSelect={select} disabled={saving}/>
                </Paper>
                <Box sx={{minWidth: 0}}>
                    <TabOverview tabValue={tabValue} tenantId={tenantType === "IMPORTER" ? tenantId : null}
                        onProducts={tenantType === "IMPORTER" ? () => select("products") : undefined}
                        onOrders={tenantType === "IMPORTER" ? status => navigate(`/distributor/my-orders${status ? `?status=${status}` : ""}`) : undefined}/>
                    <TabAnalytics tabValue={tabValue} tenantId={tenantId} userIdentity={userIdentity}/>
                    <TabOurProducts tabValue={tabValue} uploadRequest={workflowRequest?.type === "upload" ? workflowRequest.id : 0}
                        onWorkflowOpened={workflowOpened} onEditStateChange={setProductWork}/>
                    <TabMyCoupons tabValue={tabValue} createRequest={workflowRequest?.type === "coupon" ? workflowRequest.id : 0}
                        onWorkflowOpened={workflowOpened}/>
                    <TabProfileSettings tabValue={tabValue} editMode={profile.editMode} saving={profile.saving} errors={profile.errors}
                        tenantId={tenantId} tenantType={distributor.tenantType} distributor={distributor}
                        logoUrl={logoUrl} onLogoUpdate={handleLogoUpdate} onWorkingHoursUpdate={setDistributor}
                        editedDistributor={profile.draft} onEditToggle={profile.toggleEdit} onSave={profile.save}
                        onInputChange={onInputChange} onAddressChange={onAddressChange}/>
                </Box>
            </> : <Typography variant="h6" sx={{textAlign: "center", py: 4}}>{t("DistributorNotFound")}</Typography>}
    </PageContainer>;
}
